<script setup lang="ts">
/**
 * The floating panel behind the kit's menus (context menu, ribbon menus): a
 * `role="menu"` list in the popover top layer, placed against a `reference`
 * (an element, or a virtual point) with flip and shift so it stays inside the
 * viewport.
 *
 * While open it owns the menu behaviour: focus moves to the first item,
 * ArrowUp/ArrowDown/Home/End walk the items, Escape and Tab close, a pointer
 * down outside, a page scroll (not the panel's own) and a resize close, and on
 * close focus returns to where it came from. Items (`NxContextMenuItem`, or
 * any `role="menuitem"` button) close the menu through the injected context.
 *
 * Internal building block — use NxContextMenu or the ribbon menu buttons.
 */
import { computed, nextTick, onBeforeUnmount, provide, ref, watch, type Ref } from 'vue';
import { useFloatingPanel } from '../Popover';
import { useTableFocusClaim } from '../Table/focusScope';
import type { Placement } from '@floating-ui/vue';
import { menuItemsOf, moveMenuFocus, nxMenuContextKey, setTopLayer } from './menuContext';

export type NxMenuCloseReason = 'select' | 'escape' | 'outside' | 'scroll' | 'resize' | 'tab';

const props = withDefaults(
  defineProps<{
    open: boolean;
    /** What the panel is placed against: an element or a virtual point. */
    reference: HTMLElement | { getBoundingClientRect: () => DOMRect } | null;
    placement?: Placement;
    offset?: number;
    ariaLabel?: string;
    /** Width of the panel. */
    width?: 'sm' | 'md' | 'lg' | 'auto';
    /** Elements whose pointer-downs do not count as „outside“ (the trigger). */
    ignore?: readonly (HTMLElement | null | undefined)[];
    /** Where focus goes on close; defaults to the element focused before opening. */
    returnFocusTo?: HTMLElement | null;
    /** Focus the first item on open. Off for menus opened by pointer hover. */
    autoFocus?: boolean;
    /** The area the menu keeps inside instead of the viewport (see useFloatingPanel). */
    boundary?: HTMLElement | null;
  }>(),
  {
    placement: 'bottom-start',
    offset: 4,
    ariaLabel: undefined,
    width: 'md',
    ignore: () => [],
    returnFocusTo: null,
    autoFocus: true,
    boundary: null,
  }
);

const emit = defineEmits<{ close: [reason: NxMenuCloseReason] }>();

const panel = ref<HTMLElement | null>(null);
// Opened from a table (an editor in a cell), the menu is still part of that
// table's editing wherever it renders — like the kit's popovers.
useTableFocusClaim(panel, () => props.open);
const reference = computed(() => props.reference as HTMLElement | null);
const { floatingStyles, update } = useFloatingPanel(reference as Ref<HTMLElement | null>, panel, {
  open: () => props.open,
  placement: () => props.placement,
  offset: () => props.offset,
  fitHeight: true,
  boundary: () => props.boundary,
});

let restoreFocus: HTMLElement | null = null;

function close(reason: NxMenuCloseReason) {
  emit('close', reason);
}

provide(nxMenuContextKey, { close: () => close('select') });

function inside(target: EventTarget | null): boolean {
  const node = target as Node | null;
  if (!node) return false;
  if (panel.value?.contains(node)) return true;
  return props.ignore.some((el) => el?.contains(node));
}

function onPointerDown(event: PointerEvent) {
  if (!inside(event.target)) close('outside');
}
function onScroll(event: Event) {
  if (!panel.value?.contains(event.target as Node | null)) close('scroll');
}
function onResize() {
  close('resize');
}

function listen(on: boolean) {
  const method = on ? 'addEventListener' : 'removeEventListener';
  window[method]('pointerdown', onPointerDown as EventListener, true);
  window[method]('scroll', onScroll, true);
  window[method]('resize', onResize);
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      restoreFocus = (document.activeElement as HTMLElement | null) ?? null;
      listen(true);
      await nextTick();
      if (!props.open || !panel.value) return;
      setTopLayer(panel.value, true);
      update();
      if (props.autoFocus) menuItemsOf(panel.value)[0]?.focus({ preventScroll: true });
      else panel.value?.focus({ preventScroll: true });
    } else {
      listen(false);
      const target = props.returnFocusTo ?? restoreFocus;
      restoreFocus = null;
      // Only pull focus back if it is still in the (now gone) menu or lost.
      const active = document.activeElement;
      if (target && (!active || active === document.body || panel.value?.contains(active))) {
        target.focus?.({ preventScroll: true });
      }
    }
  },
  { immediate: true, flush: 'post' }
);

onBeforeUnmount(() => {
  listen(false);
  setTopLayer(panel.value, false);
});

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    close('escape');
    return;
  }
  if (event.key === 'Tab') {
    close('tab');
    return;
  }
  if (moveMenuFocus(panel.value, event.key)) event.preventDefault();
}

/** A click on any plain button inside closes the menu, like a menu item. */
function onClick(event: MouseEvent) {
  const button = (event.target as HTMLElement | null)?.closest('button');
  if (!button || button.disabled) return;
  if (button.closest('[role="menu"]') !== panel.value) return;
  if (button.getAttribute('aria-haspopup') || button.dataset.keepOpen !== undefined) return;
  if (button.dataset.nxMenuItem !== undefined) return; // items close themselves
  close('select');
}
</script>

<template>
  <div
    v-if="props.open"
    ref="panel"
    class="nx-menu-panel"
    :class="`width-${props.width}`"
    popover="manual"
    role="menu"
    tabindex="-1"
    :aria-label="props.ariaLabel"
    :style="floatingStyles"
    @keydown="onKeydown"
    @click="onClick"
    @contextmenu.prevent
  >
    <slot />
  </div>
</template>

<style scoped>
/* Floating UI owns left/top. Reveal only after its top-layer measurement. */
.nx-menu-panel {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: calc(var(--nxt-hairline) * 2);
  margin: 0;
  /* At most the room left beside the anchor (useFloatingPanel `fitHeight`),
     so a long menu scrolls instead of running off the screen. */
  max-height: min(
    calc(var(--nxt-size-unit) * 36),
    calc(100vh - var(--nxt-size-unit)),
    var(--floating-available-height, 100vh)
  );
  padding: var(--nxt-space-2xs);
  overflow-y: auto;
  overscroll-behavior: contain;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-raised-bg);
  color: var(--nxt-menu-item-fg, var(--nxt-fg));
  box-shadow: var(--nxt-shadow-lg);
  outline: none;
  user-select: none;
}
/* A column that scrolls: its entries keep their height. Shrinking them made an
   entry with a second line run into the next one once the menu overflowed. */
.nx-menu-panel > :deep(*) {
  flex-shrink: 0;
}
.nx-menu-panel.width-sm {
  width: var(--nxt-panel-width-sm);
}
.nx-menu-panel.width-md {
  width: calc(var(--nxt-size-unit) * 16);
}
.nx-menu-panel.width-lg {
  width: var(--nxt-panel-width-lg);
}
.nx-menu-panel.width-auto {
  min-width: var(--nxt-panel-width-xs);
  max-width: var(--nxt-panel-width-2xl);
}
</style>
